import React from "react";

import { SectionRetryButton } from "../../../../../components/SectionRetryButton";
import { Skeleton } from "../../../../../components/Skeleton";
import { Caption, Card, CardHeader, CardTitle } from "../../../styles";
import { SECTION_LABELS } from "../../constants";
import { SectionNotice } from "../../../styles";
import { AttentionRows, EmptyAttention } from "../../styles";
import { SectionKey } from "../../types";
import { RefreshFailedNotice } from "../../../components/RefreshFailedNotice";
import { AttentionRow } from "./AttentionRow";
import { AttentionListProps } from "./types";

// These two have no panel of their own, so this list is the only place to report a failed refresh.
const LIST_ONLY_SECTIONS: SectionKey[] = ["coverageImpact", "todo"];

export const AttentionList: React.FC<AttentionListProps> = ({
  items,
  sections,
  retries,
}) => {
  const keys = Object.keys(sections) as SectionKey[];
  const states = Object.values(sections);
  const unavailableKeys = keys.filter(
    (key) => sections[key].state === "unavailable",
  );
  const refreshFailedKeys = LIST_ONLY_SECTIONS.filter((key) => {
    const section = sections[key];
    return section.state === "available" && section.isRefreshFailed;
  });
  const isEmpty = items.length === 0;
  const isLoading = isEmpty && states.some(({ state }) => state === "loading");
  const isAllClear =
    isEmpty && states.every(({ state }) => state === "available");

  return (
    <Card aria-label="Needs attention" data-testid="admin-attention">
      <CardHeader>
        <CardTitle>Needs attention</CardTitle>
      </CardHeader>
      {unavailableKeys.map((key) => (
        <SectionNotice key={key} role="status">
          <span>
            {SECTION_LABELS[key]} is unavailable, so this list may be
            incomplete.
          </span>
          <SectionRetryButton onClick={retries[key]} />
        </SectionNotice>
      ))}
      {refreshFailedKeys.map((key) => (
        <RefreshFailedNotice
          key={key}
          sectionName={SECTION_LABELS[key]}
          onRetry={retries[key]}
        />
      ))}
      {isLoading && <Skeleton variant="rectangular" height="6rem" />}
      {isAllClear && (
        <EmptyAttention>
          <strong>All work reviewed</strong>
          <Caption>
            New requests and maintenance tasks will appear here.
          </Caption>
        </EmptyAttention>
      )}
      {items.length > 0 && (
        <AttentionRows>
          {items.map((item) => (
            <AttentionRow key={item.kind} item={item} />
          ))}
        </AttentionRows>
      )}
    </Card>
  );
};
